// ============================================
// pages/supplier/SupplierClaims.tsx
// Per-supply Bill Claims (proforma flow). Supplier files invoice + claim block,
// then DM/MP certifies and CAO releases payment. Same row-set as before.
// ============================================
import React, { useEffect, useState, useCallback } from 'react';
import { AlertCircle, CheckCircle, Pencil, Eye } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { reportsAPI, gunnyUtilizationAPI, seasonsAPI, GunnyProformaRow } from '../../api/services';
import { GunnyStepTracker } from '../../components/gunny/GunnyStepTracker';
import { GunnyRowDetail } from '../../components/gunny/GunnyRowDetail';
import { Pagination } from '../../components/common/Pagination/Pagination';
import { usePagination } from '../../components/common/Pagination/usePagination';

interface EditState {
  id: number; seasonId: number; bags_supplied: number;
  supplier_invoice_no: string; supplier_invoice_date: string;
  eway_bill_no: string; bill_submission_date: string;
  bill_raised_bags: number; claimed_rate_per_bag_incl_gst: number;
}

export const SupplierClaims: React.FC = () => {
  const { user } = useAuth();
  const [seasonId, setSeasonId] = useState<number | null>(null);
  const [rows, setRows] = useState<GunnyProformaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [detail, setDetail] = useState<GunnyProformaRow | null>(null);
  const [saving, setSaving] = useState(false);
  const { page, pageSize, totalPages, totalItems, pagedItems, setPage, setPageSize } =
    usePagination(rows, 25);

  const load = useCallback(async (sid: number) => {
    setLoading(true); setErr(null);
    try {
      const { data } = await reportsAPI.gunniesProforma({ season_id: sid });
      setRows(data.rows);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Failed to load your claims');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    seasonsAPI.active().then(({ data }) => {
      if (data?.id) { setSeasonId(data.id); load(data.id); }
    }).catch(() => setLoading(false));
  }, [load]);

  const openEditor = (r: GunnyProformaRow) => {
    if (!seasonId) return;
    setEdit({
      id: r.id, seasonId,
      bags_supplied: r.bags_supplied,
      supplier_invoice_no: r.supplier_invoice_no || '',
      supplier_invoice_date: r.supplier_invoice_date || '',
      eway_bill_no: r.eway_bill_no || '',
      bill_submission_date: r.bill_submission_date || new Date().toISOString().slice(0, 10),
      bill_raised_bags: r.bill_raised_bags || r.bags_supplied || 0,
      claimed_rate_per_bag_incl_gst: r.claimed_rate_per_bag_incl_gst || 0,
    });
  };

  const save = async () => {
    if (!edit) return;
    setSaving(true); setErr(null);
    try {
      await gunnyUtilizationAPI.supplierClaim(edit.seasonId, edit.id, {
        supplier_invoice_no: edit.supplier_invoice_no || undefined,
        supplier_invoice_date: edit.supplier_invoice_date || undefined,
        eway_bill_no: edit.eway_bill_no || undefined,
        bill_submission_date: edit.bill_submission_date || undefined,
        bill_raised_bags: edit.bill_raised_bags || undefined,
        claimed_rate_per_bag_incl_gst: edit.claimed_rate_per_bag_incl_gst || undefined,
      });
      setFlash('Claim submitted. The District Manager will certify it next.');
      setEdit(null);
      if (seasonId) await load(seasonId);
      setTimeout(() => setFlash(null), 2500);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const fmtMoney = (v: number | null) =>
    v == null ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900">Bill Claims</h1>
        <p className="text-sm text-gray-600 mt-1">
          File your invoice and bill-claim details for each batch of gunny bags supplied to a society. Values marked
          {' '}<span className="italic text-gray-500">(auto)</span> are computed on submission.
        </p>
      </div>

      {err && <div className="mb-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm"><AlertCircle className="w-4 h-4" /> {err}</div>}
      {flash && <div className="mb-3 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm"><CheckCircle className="w-4 h-4" /> {flash}</div>}

      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
        {loading ? <div className="p-10 text-center text-gray-500">Loading…</div>
          : rows.length === 0 ? <div className="p-10 text-center text-gray-500">No supply records found against your account for the active season.</div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr className="text-left text-xs uppercase text-gray-600">
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">District</th>
                    <th className="px-3 py-2">Society</th>
                    <th className="px-3 py-2 text-right">Supplied</th>
                    <th className="px-3 py-2">Invoice No.</th>
                    <th className="px-3 py-2 text-right">Bill Bags</th>
                    <th className="px-3 py-2 text-right">Rate</th>
                    <th className="px-3 py-2 text-right">Claimed Gross</th>
                    <th className="px-3 py-2 text-right">DM Gross</th>
                    <th className="px-3 py-2 text-right">Paid</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedItems.map((r) => (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">{r.sl_no}</td>
                      <td className="px-3 py-2">{r.district}</td>
                      <td className="px-3 py-2">{r.society_name || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.bags_supplied.toLocaleString()}</td>
                      <td className="px-3 py-2">{r.supplier_invoice_no || <span className="text-gray-400">—</span>}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.bill_raised_bags.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.claimed_rate_per_bag_incl_gst)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.claimed_gross_amount)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.gross_amount)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.payment_released)}</td>
                      <td className="px-3 py-2"><GunnyStepTracker row={r} /></td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => setDetail(r)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded mr-1">
                          <Eye className="w-3 h-3" /> View
                        </button>
                        {r.status === 'draft' ? (
                          <button onClick={() => openEditor(r)}
                            className="inline-flex items-center gap-1 px-3 py-1 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded">
                            <Pencil className="w-3 h-3" /> Claim
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">locked</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        {!loading && rows.length > 0 && (
          <div className="px-3 py-3 border-t bg-gray-50">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              itemLabel="claims"
            />
          </div>
        )}
      </div>

      <p className="mt-3 text-xs text-gray-500">
        Signed in as <span className="font-medium">{user?.name}</span>. You only see claims filed against your own supplier account.
      </p>

      {edit && <ClaimModal edit={edit} setEdit={setEdit} save={save} saving={saving} onClose={() => setEdit(null)} />}
      <GunnyRowDetail row={detail} onClose={() => setDetail(null)} onRowUpdated={() => { if (seasonId) load(seasonId); }} />
    </div>
  );
};

const ClaimModal: React.FC<{
  edit: EditState; setEdit: (e: EditState) => void;
  save: () => void; saving: boolean; onClose: () => void;
}> = ({ edit, setEdit, save, saving, onClose }) => {
  const gross = +(edit.bill_raised_bags * edit.claimed_rate_per_bag_incl_gst || 0).toFixed(2);
  const cgst = +(gross / 21 / 2).toFixed(2);
  const net = +(gross - cgst * 2).toFixed(2);
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
        <div className="px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">File bill claim</h2>
          <p className="text-xs text-gray-500 mt-0.5">Against {edit.bags_supplied.toLocaleString()} bags supplied.</p>
        </div>
        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice No.</label>
            <input type="text" value={edit.supplier_invoice_no}
              onChange={(e) => setEdit({ ...edit, supplier_invoice_no: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
            <input type="date" value={edit.supplier_invoice_date}
              onChange={(e) => setEdit({ ...edit, supplier_invoice_date: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-way Bill No.</label>
            <input type="text" value={edit.eway_bill_no}
              onChange={(e) => setEdit({ ...edit, eway_bill_no: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bill Submission Date</label>
            <input type="date" value={edit.bill_submission_date}
              onChange={(e) => setEdit({ ...edit, bill_submission_date: e.target.value })}
              className="w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bags on Bill</label>
            <input type="number" min={0} value={edit.bill_raised_bags}
              onChange={(e) => setEdit({ ...edit, bill_raised_bags: Number(e.target.value) || 0 })}
              className="w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rate per bag (incl. GST)</label>
            <input type="number" step="0.01" min={0} value={edit.claimed_rate_per_bag_incl_gst}
              onChange={(e) => setEdit({ ...edit, claimed_rate_per_bag_incl_gst: Number(e.target.value) || 0 })}
              className="w-full px-3 py-2 border rounded text-sm" />
          </div>
        </div>
        <div className="px-6 pb-4 border-t pt-3 text-sm bg-gray-50">
          <div className="flex justify-between">
            <span className="text-gray-600">Gross <span className="text-gray-400 italic">(auto)</span></span>
            <span className="tabular-nums font-semibold">₹ {gross.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-xs text-gray-600 mt-0.5">
            <span>CGST ≈ SGST ≈ Gross / 42</span>
            <span className="tabular-nums">₹ {cgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })} each</span>
          </div>
          <div className="flex justify-between text-xs text-gray-600 mt-0.5">
            <span>Net</span>
            <span className="tabular-nums">₹ {net.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">Cancel</button>
          <button onClick={save} disabled={saving || edit.bill_raised_bags <= 0 || edit.claimed_rate_per_bag_incl_gst <= 0}
            className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded disabled:opacity-50">
            {saving ? 'Saving…' : 'Submit claim'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierClaims;
